import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { AxiosError } from 'axios';
import { CallResult } from '../../../Api/callExternalApi';
import {
  ChangeCustomFieldTypeError,
  ChangeCustomFieldTypeRequest,
  IncompatibleCustomFieldValue,
  changeCustomFieldTypeApi
} from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { CustomFieldDefinition } from '../../../types';

type PendingState = {
  incompatibleValues: IncompatibleCustomFieldValue[];
  message: string;
  resolve: (force: boolean) => void;
};

const extractConflictBody = (errorResponse?: AxiosError): ChangeCustomFieldTypeError | null => {
  const status = errorResponse?.response?.status;
  if (status !== 409) return null;
  const data = errorResponse?.response?.data as ChangeCustomFieldTypeError | undefined;
  if (data?.error !== 'incompatible_values') return null;
  return data;
};

// valuesLabel qualifies the "existing … values" dialog copy; omit it when fields span multiple entities.
export const useChangeFieldTypeWithConfirm = (valuesLabel?: string) => {
  const [pending, setPending] = useState<PendingState | null>(null);
  const pendingRef = useRef<PendingState | null>(null);

  useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    []
  );

  const change = async (
    id: string,
    request: ChangeCustomFieldTypeRequest
  ): Promise<CallResult<CustomFieldDefinition> & { cancelled?: boolean }> => {
    const first = await changeCustomFieldTypeApi(id, request);
    if (first.error) {
      const conflict = extractConflictBody(first.error.errorResponse);
      if (!conflict) return first;

      const force = await new Promise<boolean>((resolve) => {
        const state: PendingState = {
          incompatibleValues: conflict.incompatible_values ?? [],
          message: conflict.message,
          resolve
        };
        pendingRef.current = state;
        setPending(state);
      });

      pendingRef.current = null;
      setPending(null);
      // Declining is a deliberate no-op — callers stop quietly instead of surfacing the 409 body.
      if (!force) return { ...first, cancelled: true };

      return await changeCustomFieldTypeApi(id, { ...request, force: true });
    }
    return first;
  };

  const handleConfirm = () => pendingRef.current?.resolve(true);
  const handleCancel = () => pendingRef.current?.resolve(false);

  const previewValues = pending?.incompatibleValues.slice(0, 5).map((v) => v.value) ?? [];
  const remaining = (pending?.incompatibleValues.length ?? 0) - previewValues.length;

  const dialog = (
    <Dialog
      open={pending !== null}
      onClose={handleCancel}>
      <DialogTitle>Clear incompatible values?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          {pending?.incompatibleValues.length ?? 0} existing {valuesLabel ? `${valuesLabel} ` : ''}
          {(pending?.incompatibleValues.length ?? 0) === 1 ? 'value does' : 'values do'} not match the new data type and
          will be cleared if you continue.
          {previewValues.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {previewValues.map((v, i) => (
                <li key={i}>{v}</li>
              ))}
              {remaining > 0 && <li>…and {remaining} more</li>}
            </ul>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained">
          Clear and continue
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { change, dialog };
};
